import { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from "react";
import { CurrencyCode } from "../types/currency";
import { LoadingSpinner } from "../components/BaseComponents";
import { UserRole } from "../types/UserRole";
import { AccessConfig } from "../types/AccessConfig";
import { matchPath, Navigate, useLocation, useNavigate } from "react-router-dom";
import routes from "../components/Router/routes";
import TrainingNotStarted from "../pages/TrainingNotStarted";
import VerifyEmailRequired from "../pages/VerifyEmailRequired";
import { MembershipSummary } from "../pages/SelectOrganisation";
import OrganisationPickerLayout from "../components/OrganisationPickerLayout/OrganisationPickerLayout";
import useAddNotification from "../hooks/useAddNotification";
import { notificationContext } from "./NotificationContext";
import { hasNoVisibleOrganisation } from "../lib/sampleState";
import { ASSUMPTION_REFUSED_EVENT, SESSION_EXPIRED_EVENT, ACCOUNT_LOCKED_EVENT, ACCOUNT_LOCK_CODES, ACCESS_AUDIT_UNAVAILABLE_EVENT, READ_ONLY_ACCESS_EVENT, CONNECTION_LOST_EVENT, CONNECTION_RESTORED_EVENT, bumpSessionGeneration, getSessionGeneration } from "../lib/api";
import { clearSessionState } from "../redux/store";
import { useAppDispatch } from "../redux/hooks/reduxHooks";

const SESSION_RECHECK_INTERVAL_MS = 60 * 1000;

export type UserData = {
    email: string,
    fullName: string,
    companyId: string,
    companyName: string,
    role: UserRole,
    hasCompletedSetup: boolean,
    currencyCode: CurrencyCode,
    access?: AccessConfig,
    requestedUpgradeAt?: string | null,
    // Server-derived Limited-user rule (no active real membership); never re-derived here.
    canRequestUpgrade?: boolean,
    // B-6b: false until the user proves ownership of their email address.
    emailVerified?: boolean,
    // The caller's own active memberships (B-7 switcher), with fictitiousKind for each company.
    memberships?: MembershipSummary[],
    // Global, UI-only preference: whether demo/training/sample organisations appear in the
    // organisation switcher and sign-in picker. Never gates selecting one server-side.
    showFictitiousOrganisations?: boolean,
    original: {
        companyId: string,
        companyName: string,
    }
}

type UserContextValue = {
    userData?: UserData;
    companyAssumption?: CompanyAssumption|null;
    setCompanyAssumption?: (companyAssumption: CompanyAssumption|null) => void;
    fetchUserData: () => Promise<void>;
    checkAccess: (accessKey: string) => boolean;
    clearUserData: () => void;
}

type CompanyAssumption = {
    type: 'admin'|'delegated',
    companyId: string,
};

const userContext = createContext<UserContextValue>({
    checkAccess: () => false,
    fetchUserData: async () => {},
    clearUserData: () => {},
});

function UserProvider({children}: PropsWithChildren) {
    const [userData, setUserData] = useState<UserData>();
    const [initialLoading, setInitialLoading] = useState<boolean>(true)
    const [error, setError] = useState<any>(null)
    const notify = useAddNotification();
    const { clearNotifications } = useContext(notificationContext);
    const dispatch = useAppDispatch();
    const [companyAssumption, setCompanyAssumption] = useState<CompanyAssumption|null>(null);
    const assumptionRef = useRef<CompanyAssumption|null>(null);
    assumptionRef.current = companyAssumption;
    // Issue #759 (security review correction, F1-F7 follow-up): clearUserData() resets
    // companyAssumption to null, which (via the [companyAssumption] effect below) would
    // otherwise immediately issue a fresh /me call under the new, post-clear generation - a
    // generation the F4 guard correctly treats as current, not stale. If the server's own
    // session is still alive at that moment (the best-effort logout POST failed, timed out, or
    // simply hasn't been processed yet), that call would succeed and silently re-authenticate a
    // user who just explicitly signed out. This one-shot flag lets that single, self-triggered
    // refetch be skipped without affecting any other legitimate companyAssumption change
    // (switching assumption, or dropping a refused one while staying signed in). Uses
    // assumptionRef (not the companyAssumption closure variable) because clearUserData is
    // registered once, at mount, inside an effect with an empty dependency array below - a plain
    // closure over companyAssumption would always see its initial (null) value, not the current
    // one, by the time this runs.
    const skipNextAssumptionFetchRef = useRef(false);

    // Issue #759: the single shared place every sign-out path (explicit logout, session expiry,
    // account lock, a bare 401/403 on /me) goes through, so Redux's slices are flushed in lockstep
    // with the user context - no previous session's tables/summaries can survive into a re-login.
    const clearUserData = () => {
        setUserData(undefined);
        // Toasts are in-memory and survive client-side navigation, so a previous user's
        // notifications must not remain readable on the login screen (expire() adds its own after).
        clearNotifications();
        // Security review correction: a leftover delegated/admin assumption must not survive a
        // sign-out, or it would be replayed (via fetchUserData's assumed-company-id header) onto
        // the next login in the same tab - a cross-session access-scope leak, not just stale data.
        //
        // Only arm the skip when there is an assumption to actually clear: setCompanyAssumption(null)
        // when it is already null is a same-value update React does not re-render for, so the
        // [companyAssumption] effect below would never fire to consume the flag - left armed, it
        // would then wrongly swallow the *next*, unrelated, legitimate assumption change instead.
        if (assumptionRef.current) {
            skipNextAssumptionFetchRef.current = true;
        }
        setCompanyAssumption(null);
        // Security review correction: bump before dispatching the reset so any request already in
        // flight (started under the old session) is guaranteed to see a stale generation by the
        // time its response arrives - see request()'s generation checks in lib/api.ts.
        bumpSessionGeneration();
        dispatch(clearSessionState());
    }

    const checkAccess = (accessKey: string) => {
        if(!userData) {
            return false;
        }

        const accessStrings: Record<string, boolean> = {
            'user.write': userData.access?.user.permissions.write || false,
            'user.manage': userData.access?.user.permissions.manage || false,
            'company.admin': userData.access?.company.permissions.admin || false,
            'company.portfolios': userData.access?.company.permissions.portfolios || false,
            'company.projects': userData.access?.company.permissions.projects || false,
            'company.invite': userData.access?.company.permissions.invite || false,
            'company.accounting': userData.access?.company.permissions.accounting || false,
            'company.insights': userData.access?.company.permissions.insights || false,
            'company.clientAccounting': userData.access?.company.permissions.clientAccounting || false,
        };

        return accessStrings[accessKey];
    }

    const navigate = useNavigate();
    const location = useLocation();


    // An API call was refused mid-session (e.g. the organisation withdrew admin access): leave the
    // assumed organisation through the normal clear route, which also picks the right dashboard.
    useEffect(() => {
        const onRefused = () => {
            if(!assumptionRef.current) {
                return;
            }
            notify({ body: 'You do not have permission to act as that organisation.' })
            navigate(routes.clearAssumedCompany.path, { replace: true })
        }
        window.addEventListener(ASSUMPTION_REFUSED_EVENT, onRefused)
        return () => window.removeEventListener(ASSUMPTION_REFUSED_EVENT, onRefused)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // D8 (docs/todo/user-org-membership-i-fixes-B.md): a new access session could not be recorded.
    // Distinct from ASSUMPTION_REFUSED_EVENT - the assumption itself is still valid, so this only
    // notifies, it does not clear the assumption or navigate away.
    useEffect(() => {
        const onAuditUnavailable = () => {
            notify({ body: 'Unable to record this access session. Please try again.' })
        }
        window.addEventListener(ACCESS_AUDIT_UNAVAILABLE_EVENT, onAuditUnavailable)
        return () => window.removeEventListener(ACCESS_AUDIT_UNAVAILABLE_EVENT, onAuditUnavailable)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const onReadOnly = () => {
            notify({ body: 'You have view-only access to this organisation, so this change was not saved.' })
        }
        window.addEventListener(READ_ONLY_ACCESS_EVENT, onReadOnly)
        return () => window.removeEventListener(READ_ONLY_ACCESS_EVENT, onReadOnly)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const userDataRef = useRef<UserData>();
    userDataRef.current = userData;
    const lastSessionCheckRef = useRef(Date.now());

    // The server session expires on its own, but a tab left open keeps showing the last screen
    // because nothing asks again: sign out when any call reports the session gone, and re-check
    // when the tab is shown again after a while.
    useEffect(() => {
        // Issue #759: both paths share clearUserData() (flushes userData + Redux state) and only
        // differ in the notification/redirect state, so delegated-access recovery and any future
        // push-channel fallback (#767) can reuse the same signing-out-and-redirecting shape.
        const signOutAndRedirect = (redirectState?: Record<string, unknown>) => {
            if(!userDataRef.current) {
                return false;
            }
            clearUserData()
            navigate(routes.publicLogin.path, { replace: true, state: redirectState })
            return true;
        }
        const expire = () => {
            if(signOutAndRedirect()) {
                notify({ body: 'Your session has expired. Please log in again.' })
            }
        }
        const lockOut = () => {
            signOutAndRedirect({ accountOnHold: true })
        }
        const recheck = async () => {
            if(document.visibilityState !== 'visible' || !userDataRef.current) {
                return;
            }
            if(Date.now() - lastSessionCheckRef.current < SESSION_RECHECK_INTERVAL_MS) {
                return;
            }
            lastSessionCheckRef.current = Date.now()
            // Issue #759 (final review F4): captured before the request goes out - this call sits
            // outside lib/api.ts's own generation guard (it's a raw fetch(), not request()), so it
            // needs its own check below before acting on whatever comes back.
            const requestGeneration = getSessionGeneration()
            try {
                const response = await fetch(`${process.env.REACT_APP_BASE_API}/me`, { credentials: 'include' })
                if(requestGeneration !== getSessionGeneration()) {
                    // The session was cleared (and possibly a new one started) while this recheck
                    // was in flight - its expiry/lockout verdict belongs to the abandoned session
                    // and must not be applied to whatever session (or logged-out state) exists now.
                    return;
                }
                // Reaching the server at all (any status) proves the connection is up.
                window.dispatchEvent(new Event(CONNECTION_RESTORED_EVENT))
                if(response.status === 401 || response.status === 403) {
                    const body = await response.json().catch(() => undefined)
                    if(requestGeneration !== getSessionGeneration()) {
                        return;
                    }
                    if(ACCOUNT_LOCK_CODES.includes(body?.code)) {
                        lockOut()
                    } else {
                        expire()
                    }
                }
            } catch (_) {
                if(requestGeneration !== getSessionGeneration()) {
                    return;
                }
                // Offline or a transient failure is not evidence the session ended, but it is
                // evidence the connection is down - flag it via the shared connection-health
                // signal so a "connection lost" indicator replaces any stale data on screen.
                window.dispatchEvent(new Event(CONNECTION_LOST_EVENT))
            }
        }
        window.addEventListener(SESSION_EXPIRED_EVENT, expire)
        window.addEventListener(ACCOUNT_LOCKED_EVENT, lockOut)
        document.addEventListener('visibilitychange', recheck)
        window.addEventListener('focus', recheck)
        return () => {
            window.removeEventListener(SESSION_EXPIRED_EVENT, expire)
            window.removeEventListener(ACCOUNT_LOCKED_EVENT, lockOut)
            document.removeEventListener('visibilitychange', recheck)
            window.removeEventListener('focus', recheck)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchUserData = async () => {
        const headers: Record<string, string> = {};
        if(companyAssumption) {
            headers[`neoni-${companyAssumption.type}-assumed-company-id`] = companyAssumption.companyId;
        }
        // Issue #759 (final review F4): same reasoning as recheck() above - this is a raw fetch()
        // outside lib/api.ts's own generation guard, so a response for a request that started
        // before the session was cleared must not be allowed to setUserData, drop the current
        // assumption, or sign out whatever session (or logged-out state) exists by the time it
        // resolves.
        const requestGeneration = getSessionGeneration()
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_API}/me`,
                {
                    headers,
                    credentials: 'include',
                }
            )
            if(requestGeneration !== getSessionGeneration()) {
                return;
            }
            // Reaching the server at all (any status) proves the connection is up.
            window.dispatchEvent(new Event(CONNECTION_RESTORED_EVENT))
            if (!response.ok) {
                // 401/403 simply means the user is not authenticated; treat as
                // logged out rather than an error so public pages stay clean.
                if (companyAssumption && (response.status === 401 || response.status === 403)) {
                    // The organisation (or its permission) refused the assumption. The user is still
                    // signed in as themselves: drop the assumption (which re-fetches /me without the
                    // header) instead of treating them as logged out or retrying the same 403.
                    notify({ body: 'You do not have permission to act as that organisation.' })
                    setCompanyAssumption(null)
                    return
                }
                if (response.status === 401 || response.status === 403) {
                    setInitialLoading(false)
                    clearUserData()
                } else {
                    throw new Error('Failed to get user session data')
                }
            } else {
                const body = await response.json()
                if(requestGeneration !== getSessionGeneration()) {
                    return;
                }
                setUserData(body.data)
            }
        } catch (error) {
            if(requestGeneration !== getSessionGeneration()) {
                return;
            }
            // Issue #759: a failed fetch here is as much a "lost connection" as any other API
            // call - flag it via the same shared signal so a mounted banner reflects it, even
            // though this particular (bootstrap/assumption) fetch still treats it as a hard error.
            window.dispatchEvent(new Event(CONNECTION_LOST_EVENT))
            setError(error)
        } finally {
            if(requestGeneration === getSessionGeneration()) {
                setInitialLoading(false)
            }
        }
    }

    useEffect(() => {
        if (skipNextAssumptionFetchRef.current) {
            skipNextAssumptionFetchRef.current = false;
            return;
        }
        fetchUserData()
		//eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyAssumption])

    if(initialLoading) {
        return <LoadingSpinner />
    }

    if (error) {
        // Handle error here
        console.log('Usercontext error, Navigating to login')
        return <Navigate to={routes.publicLogin.path} />
    }

    const value = {
        userData,
        companyAssumption,
        setCompanyAssumption,
        fetchUserData,
        checkAccess,
        clearUserData
    };

    // #882 (blocker 1): both of these short-circuit returns previously sat before
    // <userContext.Provider>, so the pages they render (VerifyEmailRequired/TrainingNotStarted)
    // fell back to userContext's default, no-op clearUserData/fetchUserData in production - their
    // own "Log out"/"I have verified my email" controls silently did nothing. Wrapping them in the
    // same Provider used below supplies the real implementations, same as every other branch.

    // Login refuses unverified accounts (B-6d), so only a session opened before that rule can land
    // here. The backend blocks every route behind Auth for it too; the verify-email and accept-invite
    // pages stay reachable because the emailed link or invitation token is how the account becomes
    // verified.
    if (userData?.emailVerified === false && location.pathname !== routes.acceptInvite.path && location.pathname !== routes.verifyEmail.path) {
        return <userContext.Provider value={value}><VerifyEmailRequired email={userData.email} fullName={userData.fullName} /></userContext.Provider>
    }

    // Decision 11 (docs/todo/admin-user-training-sessions.md): a training user is held on this
    // screen until their session's startDate - every route behind Auth is blocked backend-side
    // too, so this is UX only, not the enforcement itself.
    if (userData?.access?.organisationCategory === 'fictitious' && userData.access.trainingHasStarted === false) {
        return <userContext.Provider value={value}><TrainingNotStarted startDate={userData.access.trainingSessionStartDate} /></userContext.Provider>
    }

    // Signed-in users without an active organisation use the reduced dashboard, including those
    // with no memberships. A single locked organisation lands here too: it is shown as unavailable.
    // Also the case when the user has hidden sample/demo/training organisations and has no real one:
    // there is nothing to show, so the picker's holding page replaces the app even with an active session.
    // Invitation and email-verification links must stay usable while signed in, otherwise the
    // picker takes over and redirects them to the dashboard.
    const isInvitationOrVerificationPage = [routes.acceptInvite.path, routes.inviteSignUp.path, routes.verifyEmail.path]
        .some((path) => matchPath(path, location.pathname))
    const hasOnlyHiddenFictitious = hasNoVisibleOrganisation(userData)
    if (userData && ((!userData.access && userData.memberships !== undefined) || hasOnlyHiddenFictitious) && !isInvitationOrVerificationPage) {
        return (
            <userContext.Provider value={value}>
                <OrganisationPickerLayout />
            </userContext.Provider>
        )
    }

    return (
        <userContext.Provider value={value}>{ children }</userContext.Provider>
    )
};

export {
    UserProvider,
    userContext,
};
